import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { updateOnboardingSession } from '../../../../services/wizardApi';
import { useCrawlerDiscoveryOverview } from '../../../../hooks/API/wizard.api';
import axiosInstance from '../../../../api/axiosInstance';
import RunDiscoveryTestCaseModal from './RunDiscoveryTestCaseModal';
import DiscoveryScreenshotGallery from './DiscoveryScreenshotGallery';
import { toast } from 'react-toast';
import {
  LuCheck, LuLayers, LuFileText, LuCode, LuPlay, LuLoader,
  LuFileCode, LuArrowLeft, LuChevronDown, LuChevronUp, LuX,
  LuPencil, LuPlus, LuTrash2, LuSave, LuRefreshCw
} from 'react-icons/lu';
import Editor from '@monaco-editor/react';
import { getUIDiscoveryStepNumbers } from '../../../../Config/Onboarding/stepFlow';
import { STEP_STATUS } from '../../../../Config/Onboarding/constant';
import '../../../../styles/wizard.css';

const formatCount = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return '0';
  return num.toLocaleString('en-US');
};

const Step10CReviewDiscovery = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    project, uiDiscovery, goToStep, previousStep, sessionId, testCreationMode,
    markStepCompleted, setStepStatus
  } = useOnboarding();
  const discoverySteps = getUIDiscoveryStepNumbers(searchParams.get('mode') || testCreationMode);

  // 'summary' = UI Discovery Complete view, 'testCases' = generated test case list view
  const [view, setView] = useState('summary');
  const [showActionsInfo, setShowActionsInfo] = useState(false);
  const [isLoadingContinue, setIsLoadingContinue] = useState(false);

  const projectId =  project?.project_id || project?.projectId || project?.id || uiDiscovery?.projectId;
  const { data: overview, isLoading: isOverviewLoading, isError: isOverviewError } = useCrawlerDiscoveryOverview(projectId);

  // Test case list state
  const [testCases, setTestCases] = useState([]);
  const [isLoadingTestCases, setIsLoadingTestCases] = useState(false);
  const [testCasesError, setTestCasesError] = useState(null);
  const [expandedTestCase, setExpandedTestCase] = useState(null);
  const [compilingTestCaseId, setCompilingTestCaseId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalTestCases, setTotalTestCases] = useState(0);

  // Script modal state
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptContent, setScriptContent] = useState('');
  const [scriptLanguage, setScriptLanguage] = useState('python');
  const [isLoadingScript, setIsLoadingScript] = useState(false);

  // Run test case modal state
  const [isRunTestCaseModalOpen, setIsRunTestCaseModalOpen] = useState(false);
  const [selectedTestCaseForRun, setSelectedTestCaseForRun] = useState(null);

  // Edit test steps state
  const [editingTestCaseId, setEditingTestCaseId] = useState(null);
  const [editSteps, setEditSteps] = useState([]);
  const [isUpdatingSteps, setIsUpdatingSteps] = useState(false);

  const toArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined || value === '') return [];
    return [value];
  };

  const summary = {
    pages: overview?.total_pages ?? 0,
    screenshots: overview?.total_screenshots ?? 0,
    elements: overview?.total_elements ?? 0,
    forms: overview?.total_forms_mapped ?? 0,
    formElements: overview?.total_form_elements ?? 0,
    buttons: overview?.total_buttons ?? 0,
    links: overview?.total_links ?? 0,
    inputs: overview?.total_inputs ?? 0
  };

  const summaryCards = [
    { label: 'Pages', count: summary.pages, color: 'var(--primary-color)' },
    { label: 'Screenshots', count: summary.screenshots, color: 'var(--warning-color)' },
    { label: 'Elements', count: summary.elements, color: 'var(--success-color)' },
    { label: 'Forms', count: summary.forms, color: 'var(--info-color)' }
  ];

  const elementBreakdown = [
    { label: 'Form Elements', count: summary.formElements, color: 'var(--warning-color)' },
    { label: 'Buttons', count: summary.buttons, color: 'var(--primary-color)' },
    { label: 'Links', count: summary.links, color: 'var(--info-color)' },
    { label: 'Input Fields', count: summary.inputs, color: 'var(--success-color)' }
  ];

  const fetchTestCases = useCallback(async (page = currentPage) => {
    if (!projectId) return;
    setIsLoadingTestCases(true);
    setTestCasesError(null);
    try {
      const response = await axiosInstance.get(
        `/test_case/cases_list?project_id=${projectId}&page=${page}&page_size=${pageSize}`
      );

      if (response?.data) {
        setTestCases(response.data);
        setTotalTestCases(response.total ?? response.data.length);
      }
    } catch (error) {
      console.error('Error fetching test cases:', error);
      setTestCasesError('Failed to fetch test cases');
      toast.error('Failed to fetch test cases');
    } finally {
      setIsLoadingTestCases(false);
    }
  }, [projectId, currentPage, pageSize]);

  useEffect(() => {
    if (view === 'testCases') {
      fetchTestCases(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentPage, projectId]);

  const totalPages = Math.max(1, Math.ceil(totalTestCases / pageSize));

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    setExpandedTestCase(null);
    handleCancelEditSteps();
    setCurrentPage(newPage);
  };

  const handleContinue = async () => {
    setIsLoadingContinue(true);
    try {
      if (sessionId) {
        await updateOnboardingSession(sessionId, {
          currentStep: '10C'
        });
      }
      setCurrentPage(1);
      setView('testCases');
    } catch (error) {
      console.error('Error proceeding to test cases:', error);
      toast.error('Failed to load test cases. Please try again.');
    } finally {
      setIsLoadingContinue(false);
    }
  };

  const handleBackToSummary = () => {
    setView('summary');
  };

  const handleBackToCrawl = () => {
    previousStep();
    navigate(`/app/onboarding?step=${discoverySteps.crawl}&mode=${discoverySteps.modeQuery}`);
  };

  const handleContinueToRun = async () => {
    setIsLoadingContinue(true);
    try {
      if (sessionId) {
        await updateOnboardingSession(sessionId, {
          currentStep: '10C'
        });
      }

      markStepCompleted(discoverySteps.review);
      setStepStatus(discoverySteps.run, STEP_STATUS.IN_PROGRESS);
      goToStep(discoverySteps.run);
      navigate(`/app/onboarding?step=${discoverySteps.run}&mode=${discoverySteps.modeQuery}`);
    } catch (error) {
      console.error('Error proceeding to execute test:', error);
      toast.error('Failed to continue. Please try again.');
    } finally {
      setIsLoadingContinue(false);
    }
  };

  const handleCompileScript = async (testCase) => {
    if (!uiDiscovery?.runId) {
      toast.error('Missing discovery run. Please re-run discovery before compiling.');
      return;
    }

    setCompilingTestCaseId(testCase.test_case_id);
    try {
      await axiosInstance.post(
        '/crawler/generate_cwral_script',
        { discovery_run_id: uiDiscovery.runId },
        { params: { test_case_id: testCase.test_case_id } }
      );
      await fetchTestCases();
    } catch (error) {
      console.error('Error compiling automation script:', error);
      toast.error(error.response?.data?.message || 'Failed to compile script. Please try again.');
    } finally {
      setCompilingTestCaseId(null);
    }
  };

  const handleViewScript = async (testCase) => {
    setSelectedScript(testCase);
    setShowScriptModal(true);
    setIsLoadingScript(true);
    setScriptLanguage('python');
    try {
      const response = await axiosInstance.get(
        `/crawlerdiscovery_test/script?test_case_id=${testCase.test_case_id}`
      );
      const data = response?.data || response;
      const content = typeof data === 'string'
        ? data
        : data?.generated_code || data?.script?.generated_code || JSON.stringify(data, null, 2);
      setScriptContent(content || '// No script available');
      if (data?.language) setScriptLanguage(String(data.language).toLowerCase());
    } catch (error) {
      console.error('Error fetching automation script:', error);
      setScriptContent('// Failed to load automation script');
      toast.error('Failed to load automation script');
    } finally {
      setIsLoadingScript(false);
    }
  };

  const handleCloseScriptModal = () => {
    setShowScriptModal(false);
    setSelectedScript(null);
    setScriptContent('');
    setScriptLanguage('python');
  };

  const handleRunTestCase = (testCase) => {
    setSelectedTestCaseForRun(testCase);
    setIsRunTestCaseModalOpen(true);
  };

  const handleCloseRunTestCaseModal = () => {
    setIsRunTestCaseModalOpen(false);
    setSelectedTestCaseForRun(null);
  };

  const getStatusStyle = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'passed':
        return { backgroundColor: 'var(--success-color-light)', color: 'var(--success-color)' };
      case 'failed':
        return { backgroundColor: 'var(--error-color-light)', color: 'var(--error-color)' };
      case 'pending':
        return { backgroundColor: 'var(--warning-color-light)', color: 'var(--warning-color)' };
      case 'active':
        return { backgroundColor: 'var(--info-color-light)', color: 'var(--info-color)' };
      default:
        return { backgroundColor: 'var(--background-color)', color: 'var(--text-secondary)' };
    }
  };

  const formatStatus = (status) => {
    if (!status) return '—';
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getStepsCount = (testCase) => {
    if (testCase?.steps?.step_description && Array.isArray(testCase.steps.step_description)) {
      return testCase.steps.step_description.length;
    }
    return 0;
  };

  const getStepsDescription = (testCase) => {
    if (testCase?.steps?.step_description && Array.isArray(testCase.steps.step_description)) {
      return testCase.steps.step_description;
    }
    return [];
  };

  const handleStartEditSteps = (testCase, index) => {
    setExpandedTestCase(index);
    setEditingTestCaseId(testCase.test_case_id);
    setEditSteps(getStepsDescription(testCase).map((s) => String(s)));
  };

  const handleCancelEditSteps = () => {
    setEditingTestCaseId(null);
    setEditSteps([]);
  };

  const handleStepTextChange = (stepIndex, value) => {
    setEditSteps((prev) => prev.map((s, i) => (i === stepIndex ? value : s)));
  };

  const handleAddStep = () => {
    setEditSteps((prev) => [...prev, '']);
  };

  const handleAddStepAfter = (stepIndex) => {
    setEditSteps((prev) => {
      const next = [...prev];
      next.splice(stepIndex + 1, 0, '');
      return next;
    });
  };

  const handleDeleteStep = (stepIndex) => {
    setEditSteps((prev) => prev.filter((_, i) => i !== stepIndex));
  };

  const handleUpdateSteps = async (testCase) => {
    const cleanedSteps = editSteps.map((s) => s.trim()).filter(Boolean);
    if (cleanedSteps.length === 0) {
      toast.error('Please add at least one test step');
      return;
    }

    setIsUpdatingSteps(true);
    try {
      await axiosInstance.put(
        `/crawler/update/steps?test_case_id=${testCase.test_case_id}`,
        {
          step_description: cleanedSteps,
          expected_result: toArray(testCase?.steps?.expected_result),
          prerequisites: toArray(testCase?.steps?.prerequisites),
          additional_notes: toArray(testCase?.steps?.additional_notes)
        }
      );
      handleCancelEditSteps();
      await fetchTestCases();
    } catch (error) {
      console.error('Error updating test steps:', error);
      toast.error(error.response?.data?.message || 'Failed to update test steps. Please try again.');
    } finally {
      setIsUpdatingSteps(false);
    }
  };

  const actionFeatures = [
    {
      icon: <LuPencil size={16} />,
      color: 'var(--warning-color)',
      title: 'Edit Steps',
      description: 'Modify, add, reorder, or remove the individual test steps for this test case.'
    },
    {
      icon: <LuCode size={16} />,
      color: 'var(--primary-color)',
      title: 'Compile / Recompile Script',
      description: 'Generate an executable automation script from the test steps using the discovered UI Map.'
    },
    {
      icon: <LuFileCode size={16} />,
      color: 'var(--info-color)',
      title: 'View Script',
      description: 'Open the generated automation script in a read-only code viewer.'
    },
    {
      icon: <LuPlay size={16} />,
      color: 'var(--success-color)',
      title: 'Run Test Case',
      description: 'Execute the compiled automation script and review the run results.'
    }
  ];

  const reviewCompletedSteps = discoverySteps.aiPreview
    ? [1, 2, 3, 4, 5, discoverySteps.aiPreview, discoverySteps.setupTools, discoverySteps.setup, discoverySteps.crawl]
    : [1, 2, 3, 4, 5, discoverySteps.setup, discoverySteps.crawl];

  const breakdownDenominator = summary.elements > 0 ? summary.elements : 1;

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={discoverySteps.review} completedSteps={reviewCompletedSteps} />

        {view === 'summary' ? (
          <WizardCard
            title="UI Discovery Complete"
            subtitle="Your application structure has been mapped"
          >
            <div style={{ marginBottom: '2rem' }}>
              {/* Success Banner */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--success-color-light)',
                borderRadius: '8px',
                border: '1px solid var(--success-color)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '2rem'
              }}>
                <LuCheck size={24} style={{ color: 'var(--success-color)', flexShrink: 0 }} />
                <div>
                  <strong style={{ color: 'var(--success-color)' }}>UI Map Ready</strong>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    {isOverviewLoading
                      ? 'Loading discovery overview...'
                      : isOverviewError
                        ? 'Discovery overview could not be loaded'
                        : `Discovered ${formatCount(summary.pages)} pages with ${formatCount(summary.elements)} UI elements`}
                  </div>
                </div>
              </div>

              {/* Discovery Summary Stats */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                {isOverviewLoading ? (
                  <div style={{
                    gridColumn: '1 / -1',
                    padding: '2rem',
                    textAlign: 'center',
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}>
                    <LuLoader size={18} className="icon-spin" />
                    Loading overview...
                  </div>
                ) : (
                  summaryCards.map((card) => (
                    <div
                      key={card.label}
                      style={{
                        padding: '1.25rem',
                        backgroundColor: 'var(--card-bg)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-color)',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{
                        fontSize: '2rem',
                        fontWeight: 700,
                        color: card.color,
                        marginBottom: '0.25rem'
                      }}>
                        {formatCount(card.count)}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {card.label}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Element Breakdown */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
                  <LuLayers size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Element Breakdown
                </h3>

                <div style={{
                  padding: '1.5rem',
                  backgroundColor: 'var(--card-bg)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }}>
                  {isOverviewLoading ? (
                    <div style={{
                      padding: '1rem',
                      textAlign: 'center',
                      color: 'var(--text-secondary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}>
                      <LuLoader size={18} className="icon-spin" />
                      Loading element breakdown...
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {elementBreakdown.map((item) => (
                        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{
                            width: '110px',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: 'var(--text-secondary)'
                          }}>
                            {item.label}
                          </div>
                          <div style={{ flex: 1, position: 'relative' }}>
                            <div style={{
                              height: '24px',
                              backgroundColor: 'var(--background-color)',
                              borderRadius: '4px',
                              overflow: 'hidden',
                              border: '1px solid var(--border-color)'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min((item.count / breakdownDenominator) * 100, 100)}%`,
                                backgroundColor: item.color,
                                transition: 'width 0.5s ease'
                              }} />
                            </div>
                          </div>
                          <div style={{
                            width: '80px',
                            textAlign: 'right',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            color: item.color
                          }}>
                            {formatCount(item.count)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Crawled Screenshots */}
              <DiscoveryScreenshotGallery
                projectId={projectId}
                totalScreenshots={overview?.total_screenshots}
              />

              {/* Next Steps Options */}
              <div style={{
                padding: '1.5rem',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>
                  What's Next?
                </h3>

                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <LuCode size={20} style={{ color: 'var(--primary-color)' }} />
                      <strong style={{ fontSize: '0.9rem' }}>Compile AI Test Cases into Scripts</strong>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, paddingLeft: '1.75rem' }}>
                      Use the UI Map to automatically generate automation scripts from your AI test cases
                    </p>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <LuPlay size={20} style={{ color: 'var(--success-color)' }} />
                      <strong style={{ fontSize: '0.9rem' }}>Run Your First Test</strong>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, paddingLeft: '1.75rem' }}>
                      Execute tests to verify the UI Map and automation scripts
                    </p>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--background-color)',
                    borderRadius: '6px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <LuFileText size={20} style={{ color: 'var(--info-color)' }} />
                      <strong style={{ fontSize: '0.9rem' }}>Review UI Map Details</strong>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0, paddingLeft: '1.75rem' }}>
                      Explore the full UI Map from your project dashboard
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <WizardActions
              // showBack={true}
              onBack={handleBackToCrawl}
              showNext={true}
              onNext={handleContinue}
              nextLabel="Continue"
              nextLoading={isLoadingContinue}
            >
              {/* <button
                className="wizard-btn wizard-btn-secondary"
                onClick={handleSkipToRun}
                disabled={isLoadingContinue}
              >
                Skip to Run Test
              </button> */}
            </WizardActions>
          </WizardCard>
        ) : (
          <WizardCard
            title="Generate and execute script"
            subtitle={`${totalTestCases} test case(s) found for this project`}
          >
            {/* Action column features - functional descriptions (accordion) */}
            <div style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              marginBottom: '1.25rem',
              overflow: 'hidden'
            }}>
              <button
                type="button"
                onClick={() => setShowActionsInfo((prev) => !prev)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  padding: '0.75rem 1rem',
                  background: 'var(--card-bg)',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)'
                }}
              >
                <span>What can I do with each test case?</span>
                {showActionsInfo ? <LuChevronUp size={16} /> : <LuChevronDown size={16} />}
              </button>
              {showActionsInfo && (
                <div style={{
                  padding: '1rem',
                  borderTop: '1px solid var(--border-color)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  {actionFeatures.map((feature) => (
                    <div
                      key={feature.title}
                      style={{
                        display: 'flex',
                        gap: '0.75rem',
                        padding: '0.625rem 0.875rem',
                        backgroundColor: '#ffffff',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <span style={{ color: feature.color, flexShrink: 0, display: 'flex', alignItems: 'flex-start', paddingTop: '0.125rem' }}>
                        {feature.icon}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {feature.title}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleBackToSummary}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.875rem',
                marginBottom: '1.25rem',
                background: 'none',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-secondary)'
              }}
            >
              <LuArrowLeft size={16} />
              Back
            </button>

            {isLoadingTestCases ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem' }}>
                <LuLoader size={32} className="icon-spin" style={{ color: 'var(--primary-color)' }} />
              </div>
            ) : testCasesError ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px dashed var(--border-color)',
                color: 'var(--text-secondary)'
              }}>
                {testCasesError}
              </div>
            ) : testCases.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: 'var(--card-bg)',
                borderRadius: '8px',
                border: '1px dashed var(--border-color)',
                color: 'var(--text-secondary)'
              }}>
                No test cases found for this project
              </div>
            ) : (
              <div style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '40px 1fr 70px 90px 110px 90px 180px',
                  gap: '1rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: 'var(--card-bg)',
                  borderBottom: '1px solid var(--border-color)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase'
                }}>
                  <div></div>
                  <div>Name</div>
                  <div>Steps</div>
                  <div>Priority</div>
                  <div>Automation</div>
                  <div>Status</div>
                  <div style={{ textAlign: 'center' }}>Actions</div>
                </div>

                {/* Table Rows */}
                {testCases.map((testCase, index) => {
                  const isGenerated = testCase.automation_status === 'generated';
                  const isCompiling = compilingTestCaseId === testCase.test_case_id;

                  return (
                    <div key={testCase.test_case_id}>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '40px 1fr 70px 90px 110px 90px 180px',
                          gap: '1rem',
                          padding: '0.875rem 1rem',
                          alignItems: 'center',
                          backgroundColor: expandedTestCase === index ? 'var(--primary-color-light)' : '#fff',
                          borderBottom: index < testCases.length - 1 ? '1px solid var(--border-color)' : 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => {
                          const next = expandedTestCase === index ? null : index;
                          setExpandedTestCase(next);
                          if (next === null) handleCancelEditSteps();
                        }}
                        onMouseEnter={(e) => {
                          if (expandedTestCase !== index) e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                        }}
                        onMouseLeave={(e) => {
                          if (expandedTestCase !== index) e.currentTarget.style.backgroundColor = '#fff';
                        }}
                      >
                        {/* Status Icon */}
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: isGenerated ? 'var(--success-color)' : 'var(--border-color)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <LuCheck size={14} />
                        </div>

                        {/* Test Case Name */}
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {testCase.name}
                        </div>

                        {/* Steps Count */}
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                          {getStepsCount(testCase)} steps
                        </div>

                        {/* Priority */}
                        <div>
                          <span style={{
                            padding: '0.25rem 0.625rem',
                            backgroundColor: testCase.priority === 'High' ? 'var(--error-color-light)' : 'var(--info-color-light)',
                            color: testCase.priority === 'High' ? 'var(--error-color)' : 'var(--info-color)',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {testCase.priority || 'Medium'}
                          </span>
                        </div>

                        {/* Automation Status */}
                        <div>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: isGenerated ? 'var(--success-color-light)' : 'var(--background-color)',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: isGenerated ? 'var(--success-color)' : 'var(--text-secondary)',
                            whiteSpace: 'nowrap'
                          }}>
                            {isGenerated ? 'Generated' : 'Not generated'}
                          </span>
                        </div>

                        {/* Status */}
                        <div>
                          <span style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            textTransform: 'capitalize',
                            ...getStatusStyle(testCase.status)
                          }}>
                            {formatStatus(testCase.status)}
                          </span>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '0.375rem', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartEditSteps(testCase, index);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '0.375rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--warning-color)',
                              borderRadius: '4px'
                            }}
                            title="Edit test steps"
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef3c7'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <LuPencil size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCompileScript(testCase);
                            }}
                            disabled={isCompiling}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: isCompiling ? 'not-allowed' : 'pointer',
                              padding: '0.375rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: 'var(--primary-color)',
                              borderRadius: '4px',
                              opacity: isCompiling ? 0.6 : 1
                            }}
                            title={isGenerated ? 'Recompile and generate script' : 'Compile and generate script'}
                            onMouseEnter={(e) => { if (!isCompiling) e.currentTarget.style.backgroundColor = 'var(--primary-color-light)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            {isCompiling
                              ? <LuLoader size={16} className="icon-spin" />
                              : isGenerated
                                ? <LuRefreshCw size={16} />
                                : <LuCode size={16} />}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewScript(testCase);
                            }}
                            disabled={!isGenerated}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: isGenerated ? 'pointer' : 'not-allowed',
                              padding: '0.375rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: isGenerated ? 'var(--info-color)' : 'var(--text-secondary)',
                              borderRadius: '4px',
                              opacity: isGenerated ? 1 : 0.4
                            }}
                            title="View test script"
                            onMouseEnter={(e) => { if (isGenerated) e.currentTarget.style.backgroundColor = 'var(--info-color-light)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <LuFileCode size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunTestCase(testCase);
                            }}
                            disabled={!isGenerated}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: isGenerated ? 'pointer' : 'not-allowed',
                              padding: '0.375rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: isGenerated ? 'var(--success-color)' : 'var(--text-secondary)',
                              borderRadius: '4px',
                              opacity: isGenerated ? 1 : 0.4
                            }}
                            title="Run test case"
                            onMouseEnter={(e) => { if (isGenerated) e.currentTarget.style.backgroundColor = 'var(--success-color-light)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <LuPlay size={16} />
                          </button>
                          {expandedTestCase === index ? <LuChevronUp size={16} /> : <LuChevronDown size={16} />}
                        </div>
                      </div>

                      {/* Expanded Row - Steps Details */}
                      {expandedTestCase === index && editingTestCaseId !== testCase.test_case_id && (
                        <div style={{ padding: '1rem', backgroundColor: 'var(--background-color)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                              Test Steps ({getStepsCount(testCase)}):
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleStartEditSteps(testCase, index); }}
                              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100"
                            >
                              <LuPencil size={13} /> Edit Steps
                            </button>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {getStepsDescription(testCase).map((step, stepIndex) => (
                              <div
                                key={stepIndex}
                                style={{
                                  display: 'flex',
                                  gap: '0.75rem',
                                  padding: '0.625rem 0.875rem',
                                  backgroundColor: '#ffffff',
                                  borderRadius: '6px',
                                  border: '1px solid var(--border-color)',
                                  fontSize: '0.8125rem'
                                }}
                              >
                                <span style={{ fontWeight: 600, color: 'var(--primary-color)', minWidth: '24px' }}>
                                  {stepIndex + 1}.
                                </span>
                                <div style={{ flex: 1 }}>{step}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Expanded Row - Edit Steps Mode */}
                      {expandedTestCase === index && editingTestCaseId === testCase.test_case_id && (
                        <div
                          className="border-t border-gray-200 bg-gray-50 p-4 sm:p-5"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <h4 className="text-sm font-semibold text-gray-700">
                              Edit Test Steps
                            </h4>
                            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
                              {editSteps.length} step{editSteps.length === 1 ? '' : 's'}
                            </span>
                          </div>

                          <div className="flex flex-col gap-2">
                            {editSteps.map((step, stepIndex) => (
                              <div
                                key={stepIndex}
                                className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400"
                              >
                                <span className="mt-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-semibold text-indigo-600">
                                  {stepIndex + 1}
                                </span>
                                <textarea
                                  value={step}
                                  onChange={(e) => handleStepTextChange(stepIndex, e.target.value)}
                                  rows={2}
                                  placeholder={`Describe step ${stepIndex + 1}...`}
                                  className="min-h-[38px] flex-1 resize-y border-0 bg-transparent px-1 py-1.5 text-sm text-gray-800 outline-none placeholder:text-gray-400"
                                />
                                <div className="mt-1 flex flex-shrink-0 flex-col items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleAddStepAfter(stepIndex)}
                                    title="Add step below"
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                                  >
                                    <LuPlus size={15} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteStep(stepIndex)}
                                    title="Delete step"
                                    className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                  >
                                    <LuTrash2 size={15} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          {editSteps.length === 0 && (
                            <p className="mt-2 rounded-lg border border-dashed border-gray-300 bg-white py-4 text-center text-xs text-gray-400">
                              No steps yet. Add your first step below.
                            </p>
                          )}

                          <button
                            type="button"
                            onClick={handleAddStep}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-indigo-300 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 transition-colors hover:bg-indigo-100"
                          >
                            <LuPlus size={14} /> Add Step
                          </button>

                          <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 pt-3">
                            <button
                              type="button"
                              onClick={handleCancelEditSteps}
                              disabled={isUpdatingSteps}
                              className="rounded-md border border-gray-300 bg-white px-4 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateSteps(testCase)}
                              disabled={isUpdatingSteps}
                              className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isUpdatingSteps
                                ? <><LuLoader size={14} className="icon-spin" /> Updating...</>
                                : <><LuSave size={14} /> Update</>}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer: range on the left, pagination centered, Continue on the right */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              columnGap: '0.75rem',
              rowGap: '0.75rem',
              marginTop: '1.25rem'
            }}>
              <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', justifySelf: 'start' }}>
                {!isLoadingTestCases && !testCasesError && totalTestCases > 0 && (
                  <>
                    Showing {(currentPage - 1) * pageSize + 1}
                    {'–'}
                    {Math.min(currentPage * pageSize, totalTestCases)} of {totalTestCases}
                  </>
                )}
              </div>

              <div style={{ justifySelf: 'center', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                {!isLoadingTestCases && !testCasesError && totalTestCases > 0 && (
                  <>
                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    style={{
                      padding: '0.375rem 0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      background: '#fff',
                      color: 'var(--text-secondary)',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
                      opacity: currentPage <= 1 ? 0.5 : 1
                    }}
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => (
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - currentPage) <= 1
                    ))
                    .reduce((acc, p, i, arr) => {
                      if (i > 0 && p - arr[i - 1] > 1) acc.push('ellipsis-' + p);
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p) => (
                      typeof p === 'string' ? (
                        <span key={p} style={{ padding: '0 0.25rem', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>…</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          onClick={() => handlePageChange(p)}
                          style={{
                            minWidth: '32px',
                            padding: '0.375rem 0.5rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            background: p === currentPage ? 'var(--primary-color)' : '#fff',
                            color: p === currentPage ? '#fff' : 'var(--text-secondary)',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {p}
                        </button>
                      )
                    ))}

                  <button
                    type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                      padding: '0.375rem 0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: '6px',
                      background: '#fff',
                      color: 'var(--text-secondary)',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                      opacity: currentPage >= totalPages ? 0.5 : 1
                    }}
                  >
                    Next
                  </button>
                  </>
                )}
              </div>

              <div style={{ justifySelf: 'end' }}>
                <button
                  type="button"
                  className="wizard-btn wizard-btn-primary"
                  onClick={handleContinueToRun}
                  disabled={isLoadingContinue}
                >
                  {isLoadingContinue ? (
                    <>
                      <span className="spinner-small"></span>
                      Loading...
                    </>
                  ) : 'Continue'}
                </button>
              </div>
            </div>
          </WizardCard>
        )}
      </WizardContainer>

      {/* Script Viewer Modal */}
      {showScriptModal && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '2rem'
          }}
          onClick={handleCloseScriptModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '1200px',
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              zIndex: 1000000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #dee2e6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#333' }}>
                  <LuFileCode size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Automation Script
                </h3>
                <p style={{ margin: 0, marginTop: '0.25rem', fontSize: '0.875rem', color: '#666' }}>
                  {selectedScript?.name}
                </p>
              </div>
              <button
                onClick={handleCloseScriptModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666',
                  borderRadius: '4px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <LuX size={24} />
              </button>
            </div>

            {/* Editor */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {isLoadingScript && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <LuLoader size={32} className="icon-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
              )}
              <Editor
                height="100%"
                language={scriptLanguage}
                value={scriptContent}
                theme="vs-dark"
                options={{
                  readOnly: true,
                  minimap: { enabled: true },
                  fontSize: 14,
                  lineNumbers: 'on',
                  roundedSelection: false,
                  scrollBeyondLastLine: false,
                  automaticLayout: true,
                  tabSize: 2
                }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Run Test Case Modal */}
      {selectedTestCaseForRun && (
        <RunDiscoveryTestCaseModal
          isOpen={isRunTestCaseModalOpen}
          onClose={handleCloseRunTestCaseModal}
          testCaseId={selectedTestCaseForRun.test_case_id}
          testCaseName={selectedTestCaseForRun.name}
        />
      )}
    </PrivateWizardLayout>
  );
};

export default Step10CReviewDiscovery;
